'use client'

import { useSession } from 'next-auth/react'
import { Button } from "@/components/ui/button"
import { LayoutGrid, Users, Clock, PlayCircle, BarChart2, Share2, MoreHorizontal, Edit, Trash2, PlusCircle, Activity, PackagePlus, Loader2 } from 'lucide-react';
import { DashboardUsageInsights } from '@/components/dashboard-usage-insights'
import { PeriodChangeCaption } from '@/components/period-change-caption'
import { useState, useEffect } from 'react'
import { Card, CardContent } from "@/components/ui/card"
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import { formatDistanceToNow } from 'date-fns'
import { useRouter } from 'next/navigation'
import { cn } from "@/lib/utils"
import Link from 'next/link'
import { Skeleton } from "@/components/ui/skeleton"
import { ShareRoomModal } from "@/components/share-room-modal"
import { JourneySheet } from '@/components/journey-sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { AiChat } from '@/components/ai-chat'
import { CreateSmartRoomComponent } from './create-smart-room'
import { FeatureTour } from '@/components/feature-tour'
import { RoomListAccountLabel } from '@/components/room-list-account-label'
import { useEntitlements } from '@/components/providers/entitlements-provider'
import { SmartShareDashboardBody } from '@/components/dashboard/smart-share-dashboard-body'
import { UpgradePrompts } from '@/components/lifecycle/upgrade-prompts'

interface Room {
  id: string;
  name: string;
  roomType?: string | null;
  account: {
    id: string;
    name: string;
  };
  owner: {
    name: string;
  };
  createdAt: string;
  icon?: string;
  iconBg?: string;
  members: number;
  customerActivity: number;
  lastActivity: string;
  plays: number | 'Inactive';
  status?: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  isExample?: boolean;
  tenant?: {
    id: string;
    name: string;
  };
  domainJoinEnabled?: boolean;
  allowedJoinDomains?: string[];
}

interface ProductOption {
  id: string;
  name: string;
  productType: string;
  status: string;
  listPrice: string | null;
  currency: string | null;
  priceQuantityLabel: string | null;
}

interface RoomProductItem {
  id: string;
  roomId: string;
  productId: string;
  sortOrder: number;
  pinned: boolean;
  visibility: 'VISITOR' | 'INTERNAL_ONLY';
  product: ProductOption;
}

function getApiErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') return fallback
  const p = payload as { error?: unknown }
  if (typeof p.error === 'string') return p.error
  if (p.error && typeof p.error === 'object') {
    const fieldErrors = (p.error as { fieldErrors?: Record<string, string[]> }).fieldErrors
    if (fieldErrors) {
      const firstKey = Object.keys(fieldErrors)[0]
      if (firstKey && fieldErrors[firstKey]?.[0]) return fieldErrors[firstKey][0]
    }
  }
  return fallback
}

type ActivityGranularity = 'daily' | 'weekly' | 'monthly'

export function LandingPageComponent() {
  const { data: session, status } = useSession()
  const entitlements = useEntitlements()
  const isSmartShare = entitlements.experience === 'SMART_SHARE'
  const canStartTrial =
    isSmartShare &&
    !entitlements.activeTrial &&
    entitlements.latestTrial?.status !== 'EXPIRED' &&
    entitlements.latestTrial?.status !== 'CANCELLED' &&
    (session?.user?.role === 'ADMIN' || session?.user?.role === 'APP_OWNER')
  const showExpiredTrialBanner =
    !entitlements.activeTrial &&
    (entitlements.latestTrial?.status === 'EXPIRED' ||
      entitlements.latestTrial?.status === 'CANCELLED')
  const [rooms, setRooms] = useState<Room[]>([])
  const [sharedRooms, setSharedRooms] = useState<Room[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingSharedRooms, setIsLoadingSharedRooms] = useState(false)
  const [isStartingTrial, setIsStartingTrial] = useState(false)
  const router = useRouter()
  const [activeTab, setActiveTab] = useState("my-rooms")
  const myRoomsCount = rooms.length
  const sharedRoomsCount = sharedRooms.length
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [roomToDelete, setRoomToDelete] = useState<string | null>(null)
  const { toast } = useToast()
  const [selectedRoomForAnalytics, setSelectedRoomForAnalytics] = useState<Room | null>(null)
  const [isJourneySheetOpen, setIsJourneySheetOpen] = useState(false)
  const [analyticsStats, setAnalyticsStats] = useState<{
    activeUsers: number;
    activeUsersChange: number;
    totalRooms: number;
    totalRoomsChange: number;
    newRoomsThisMonth: number;
    contentViews: number;
    contentViewsChange: number;
    contentShares: number;
    contentSharesChange: number;
    externalEngagement: number;
    externalEngagementChange: number;
    roomVisits: number;
    roomVisitsChange: number;
    maxRooms: number;
    canCreateRoom: boolean;
  } | null>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [activityGranularity, setActivityGranularity] =
    useState<ActivityGranularity>('weekly')
  const [activityChartData, setActivityChartData] = useState<
    { name: string; customer: number; internal: number }[]
  >([])
  const [isLoadingActivityChart, setIsLoadingActivityChart] = useState(true)
  const [activityChartError, setActivityChartError] = useState(false)
  const [statusFilter, setStatusFilter] = useState<'all' | 'DRAFT' | 'ACTIVE' | 'ARCHIVED'>('all')
  const [showCreateRoom, setShowCreateRoom] = useState(false)
  const [isProductsDialogOpen, setIsProductsDialogOpen] = useState(false)
  const [selectedRoomForProducts, setSelectedRoomForProducts] = useState<Room | null>(null)
  const [roomProducts, setRoomProducts] = useState<RoomProductItem[]>([])
  const [availableProducts, setAvailableProducts] = useState<ProductOption[]>([])
  const [selectedProductId, setSelectedProductId] = useState('')
  const [isLoadingRoomProducts, setIsLoadingRoomProducts] = useState(false)
  const [isLoadingProductOptions, setIsLoadingProductOptions] = useState(false)
  const [isSavingRoomProduct, setIsSavingRoomProduct] = useState(false)
  const [removingProductId, setRemovingProductId] = useState<string | null>(null)

  useEffect(() => {
    if (isSmartShare) return
    const fetchRooms = async () => {
      if (status === 'authenticated') {
        try {
          setTimeout(async () => {
            const url = new URL('/api/rooms', window.location.origin)
            if (statusFilter !== 'all') {
              url.searchParams.set('status', statusFilter)
            }
            if (statusFilter === 'all' || statusFilter === 'ARCHIVED') {
              url.searchParams.set('includeArchived', 'true')
            }
            const response = await fetch(url.toString())
            if (!response.ok) {
              throw new Error('Failed to fetch rooms')
            }
            const data = await response.json()
            setRooms(data)
            setIsLoading(false)
          }, 100)
        } catch (error) {
          console.error('Error fetching rooms:', error)
          setIsLoading(false)
        }
      }
    }

    fetchRooms()
  }, [status, statusFilter, isSmartShare])

  useEffect(() => {
    if (isSmartShare) return
    const fetchSharedRooms = async () => {
      if (status === 'authenticated') {
        try {
          setIsLoadingSharedRooms(true)
          const response = await fetch('/api/rooms?type=shared')
          if (!response.ok) {
            throw new Error('Failed to fetch shared rooms')
          }
          const data = await response.json()
          setSharedRooms(data)
          setIsLoadingSharedRooms(false)
        } catch (error) {
          console.error('Error fetching shared rooms:', error)
          setIsLoadingSharedRooms(false)
        }
      }
    }

    fetchSharedRooms()
  }, [status, isSmartShare])

  useEffect(() => {
    if (isSmartShare) return
    const fetchAnalyticsStats = async () => {
      if (status === 'authenticated') {
        try {
          setIsLoadingStats(true)
          const response = await fetch('/api/analytics/stats')
          if (!response.ok) {
            throw new Error('Failed to fetch analytics stats')
          }
          const data = await response.json()
          setAnalyticsStats(data)
          setIsLoadingStats(false)
        } catch (error) {
          console.error('Error fetching analytics stats:', error)
          setIsLoadingStats(false)
        }
      }
    }

    fetchAnalyticsStats()
  }, [status, isSmartShare])

  useEffect(() => {
    if (isSmartShare) return
    const fetchActivityChart = async () => {
      if (status !== 'authenticated') return
      try {
        setIsLoadingActivityChart(true)
        setActivityChartError(false)
        const response = await fetch(
          `/api/analytics/monthly-activity?granularity=${activityGranularity}`
        )
        if (!response.ok) {
          throw new Error('Failed to fetch activity')
        }
        const data = await response.json()
        setActivityChartData(Array.isArray(data) ? data : [])
      } catch (error) {
        console.error('Error fetching activity chart:', error)
        setActivityChartError(true)
        setActivityChartData([])
      } finally {
        setIsLoadingActivityChart(false)
      }
    }

    fetchActivityChart()
  }, [status, activityGranularity, isSmartShare])

  const handleRoomCreated = () => {
    setShowCreateRoom(false)
    toast({
      title: "Room created",
      description: "Your new smart room is ready.",
    })
    // Refresh rooms
    const url = new URL('/api/rooms', window.location.origin)
    fetch(url.toString()).then(res => res.json()).then(data => setRooms(data))
  }

  const formatLastActivity = (timestamp: string) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
  }

  const handleRoomClick = (roomId: string) => {
    router.push(`/room/${roomId}`)
  }

  const handlePlaybookClick = (roomId: string) => {
    router.push(`/room/${roomId}/playbook`)
  }

  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center space-x-4 p-4 bg-white rounded-xl border border-slate-100 shadow-sm">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  )

  const handleEditRoom = (roomId: string) => {
    router.push(`/room/${roomId}/edit`)
  }

  const handleDeleteRoom = (roomId: string) => {
    setRoomToDelete(roomId)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!roomToDelete) return

    try {
      const response = await fetch(`/api/rooms/${roomToDelete}`, {
        method: 'DELETE',
      })

      const data = await response.json()

      if (response.ok) {
        setRooms(prevRooms => prevRooms.filter(room => room.id !== roomToDelete))
        toast({
          title: "Success",
          description: "Room deleted successfully",
          variant: "default",
        })
      } else {
        toast({
          title: "Error",
          description: data.details || "Failed to delete room",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error deleting room:', error)
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting the room",
        variant: "destructive",
      })
    } finally {
      setDeleteDialogOpen(false)
      setRoomToDelete(null)
    }
  }

  const handleShareRoom = (room: Room) => {
    if (room.account && room.account.id) {
      setSelectedRoom(room)
      setIsShareModalOpen(true)
    } else {
      console.error('Cannot share room: missing account information')
    }
  }

  const handleAnalyticsClick = (room: Room, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedRoomForAnalytics(room)
    setIsJourneySheetOpen(true)
  }

  const getActivityBadgeConfig = (lastActivity: string | null, customerActivity: number) => {
    if (customerActivity === 0 || !lastActivity) {
      return {
        className: "bg-slate-100 text-slate-600 border-slate-200",
        text: "No Activity"
      }
    }

    const now = new Date()
    const lastActivityDate = new Date(lastActivity)
    const daysSinceActivity = Math.floor((now.getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24))

    if (daysSinceActivity <= 30) {
      return {
        className: "bg-emerald-100 text-emerald-700 border-emerald-200",
        text: `${customerActivity} Activity`
      }
    }

    if (daysSinceActivity <= 90) {
      return {
        className: "bg-amber-100 text-amber-700 border-amber-200",
        text: `${customerActivity} Activity`
      }
    }

    return {
      className: "bg-slate-100 text-slate-600 border-slate-200",
      text: `${customerActivity} Activity`
    }
  }

  const fetchRoomProducts = async (roomId: string) => {
    setIsLoadingRoomProducts(true)
    try {
      const response = await fetch(`/api/rooms/${roomId}/products`)
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload.error || 'Failed to load room products')
      }
      const data = await response.json()
      setRoomProducts(data.products || [])
    } catch (error) {
      console.error('Error fetching room products:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load room products",
        variant: "destructive",
      })
    } finally {
      setIsLoadingRoomProducts(false)
    }
  }

  const fetchAvailableProducts = async () => {
    setIsLoadingProductOptions(true)
    try {
      const response = await fetch('/api/products?status=ACTIVE&limit=100&offset=0')
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(getApiErrorMessage(payload, 'Failed to load products'))
      }
      const data = await response.json()
      setAvailableProducts(data.products || [])
    } catch (error) {
      console.error('Error fetching products:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load products",
        variant: "destructive",
      })
    } finally {
      setIsLoadingProductOptions(false)
    }
  }

  const handleManageProducts = async (room: Room, event?: React.MouseEvent) => {
    event?.stopPropagation()
    setSelectedRoomForProducts(room)
    setIsProductsDialogOpen(true)
    setSelectedProductId('')
    await Promise.all([fetchRoomProducts(room.id), fetchAvailableProducts()])
  }

  const handleAddProductToRoom = async () => {
    if (!selectedRoomForProducts?.id || !selectedProductId) return
    setIsSavingRoomProduct(true)
    try {
      const response = await fetch(`/api/rooms/${selectedRoomForProducts.id}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: selectedProductId }),
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to add product')
      }

      toast({
        title: "Product added",
        description: "The product has been linked to this room.",
      })
      setSelectedProductId('')
      await fetchRoomProducts(selectedRoomForProducts.id)
    } catch (error) {
      console.error('Error adding room product:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add product to room",
        variant: "destructive",
      })
    } finally {
      setIsSavingRoomProduct(false)
    }
  }

  const handleRemoveProductFromRoom = async (productId: string) => {
    if (!selectedRoomForProducts?.id) return
    setRemovingProductId(productId)
    try {
      const response = await fetch(`/api/rooms/${selectedRoomForProducts.id}/products/${productId}`, {
        method: 'DELETE',
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to remove product')
      }
      toast({
        title: "Product removed",
        description: "The product has been removed from this room.",
      })
      await fetchRoomProducts(selectedRoomForProducts.id)
    } catch (error) {
      console.error('Error removing room product:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove product from room",
        variant: "destructive",
      })
    } finally {
      setRemovingProductId(null)
    }
  }

  const linkedProductIds = new Set(roomProducts.map((item) => item.productId))
  const unlinkedProducts = availableProducts.filter((product) => !linkedProductIds.has(product.id))

  const firstName = session?.user?.firstName || 'there'

  async function startSmartRoomsTrial() {
    setIsStartingTrial(true)
    try {
      const response = await fetch('/api/trials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'SMART_ROOMS' }),
      })
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Could not start trial')
      }
      toast({
        title: 'Smart Rooms trial started',
        description: 'You have 14 days to create and explore rooms.',
      })
      await entitlements.refresh()
      router.refresh()
    } catch (error) {
      toast({
        title: 'Trial unavailable',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      })
    } finally {
      setIsStartingTrial(false)
    }
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-3 sm:p-4 md:p-6 bg-slate-50/50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Welcome back, {firstName}</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isSmartShare
              ? 'Share documents professionally with Smart Share'
              : "Here's what's happening in your rooms"}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full md:w-auto md:flex-1 md:justify-end max-w-xl">
          {!isSmartShare && (
            <div className="w-full sm:flex-1 sm:max-w-sm">
              <AiChat roomId={null} />
            </div>
          )}
          {isSmartShare ? (
            <div className="flex flex-wrap gap-2">
              <Button asChild className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/20 h-10 px-5 rounded-xl font-medium text-sm">
                <Link href="/smart-share">
                  <Share2 className="mr-2 h-4 w-4" /> Smart Share
                </Link>
              </Button>
              {canStartTrial && (
                <Button
                  variant="outline"
                  className="rounded-xl h-10"
                  disabled={isStartingTrial}
                  onClick={() => void startSmartRoomsTrial()}
                >
                  {isStartingTrial ? 'Starting…' : 'Try Smart Rooms'}
                </Button>
              )}
            </div>
          ) : (
            <Button
              data-tour="create-room"
              onClick={() => setShowCreateRoom(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/20 transition-all duration-200 h-10 px-5 rounded-xl font-medium text-sm whitespace-nowrap"
            >
              <PlusCircle className="mr-2 h-4 w-4" /> Create Room
            </Button>
          )}
        </div>
      </div>

      {showExpiredTrialBanner && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Your Smart Rooms trial has ended. Rooms created during the trial are read-only.{' '}
          <Link href="/pricing?intent=rooms" className="font-semibold underline">
            Upgrade to Pro
          </Link>{' '}
          to reactivate editing.
        </div>
      )}

      {entitlements.activeTrial && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          Smart Rooms trial active until{' '}
          {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
            new Date(entitlements.activeTrial.endsAt)
          )}
          . You can create up to 3 rooms.
        </div>
      )}

      <UpgradePrompts />

      {isSmartShare && <SmartShareDashboardBody />}

      {!isSmartShare && (
      <>
      {/* Metrics: keep KPIs + chart visible above the fold */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          {/* Active members */}
          <Card className="relative bg-gradient-to-br from-white to-emerald-50/40 border-slate-200/80 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl overflow-hidden lg:col-span-3">
            <div className="absolute inset-y-0 left-0 w-1 bg-emerald-400/80" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-sm font-medium text-slate-500 tracking-wide">
                  Active members
                </p>
                <div className="bg-emerald-100/80 p-2 rounded-xl shrink-0">
                  <Users className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
              {isLoadingStats ? (
                <Skeleton className="h-8 w-14 mb-1" />
              ) : (
                <div className="text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">
                  {analyticsStats?.activeUsers ?? 0}
                </div>
              )}
              {!isLoadingStats && analyticsStats && (
                <PeriodChangeCaption
                  change={analyticsStats.activeUsersChange}
                  className="mt-1"
                />
              )}
            </CardContent>
          </Card>

          {/* Engagement */}
          <Card className="relative bg-gradient-to-br from-white to-violet-50/40 border-slate-200/80 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl overflow-hidden lg:col-span-3">
            <div className="absolute inset-y-0 left-0 w-1 bg-violet-400/80" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-sm font-medium text-slate-500 tracking-wide">
                  Engagement
                </p>
                <div className="bg-violet-100/80 p-2 rounded-xl shrink-0">
                  <Activity className="w-4 h-4 text-violet-600" />
                </div>
              </div>
              {isLoadingStats ? (
                <Skeleton className="h-8 w-16 mb-1" />
              ) : (
                <div className="text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">
                  {(analyticsStats?.externalEngagement ?? 0).toLocaleString()}
                </div>
              )}
              {!isLoadingStats && analyticsStats && (
                <PeriodChangeCaption
                  change={analyticsStats.externalEngagementChange}
                  className="mt-1"
                />
              )}
            </CardContent>
          </Card>

          {/* Activity (weekly / daily / monthly) */}
          <Card
            className="bg-white border-slate-200/80 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl overflow-hidden lg:col-span-6"
            data-tour="activity-chart"
          >
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="min-w-0 flex items-center gap-3">
                  <p className="text-sm font-semibold text-slate-900">Activity</p>
                  <div className="flex items-center gap-2.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      Customer
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                      Internal
                    </span>
                  </div>
                </div>
                <Select
                  value={activityGranularity}
                  onValueChange={(v) =>
                    setActivityGranularity(v as ActivityGranularity)
                  }
                >
                  <SelectTrigger className="h-8 w-[148px] text-xs bg-slate-50 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily (30 days)</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isLoadingActivityChart ? (
                <div className="h-[96px] flex items-center justify-center rounded-xl bg-slate-50/80">
                  <div className="text-center space-y-2">
                    <Skeleton className="h-7 w-7 rounded-full mx-auto" />
                    <p className="text-xs text-slate-500">Loading activity…</p>
                  </div>
                </div>
              ) : activityChartError ? (
                <div className="h-[96px] flex items-center justify-center rounded-xl bg-slate-50/80">
                  <p className="text-sm text-slate-500">Couldn&apos;t load activity. Try again later.</p>
                </div>
              ) : activityChartData.length === 0 ? (
                <div className="h-[96px] flex items-center justify-center rounded-xl bg-slate-50/80">
                  <p className="text-sm text-slate-500">No activity data for this period</p>
                </div>
              ) : (
                <div className="h-[96px] w-full min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={activityChartData}
                      margin={{
                        top: 8,
                        right: 8,
                        left: 0,
                        bottom: activityGranularity === 'daily' ? 4 : 0,
                      }}
                    >
                      <defs>
                        <linearGradient id="customerActivityFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#22c55e" stopOpacity={0.28} />
                          <stop offset="100%" stopColor="#22c55e" stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="internalActivityFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.22} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e2e8f0"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="name"
                        stroke="#94a3b8"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        interval={
                          activityGranularity === 'daily'
                            ? 5
                            : activityGranularity === 'weekly'
                              ? 1
                              : 0
                        }
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        width={32}
                      />
                      <Tooltip
                        contentStyle={{
                          fontSize: '12px',
                          backgroundColor: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          padding: '8px 12px',
                          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                        }}
                        labelStyle={{ color: '#64748b', marginBottom: 4 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="customer"
                        stroke="#16a34a"
                        strokeWidth={2.25}
                        fill="url(#customerActivityFill)"
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: '#16a34a' }}
                        name="Customer"
                      />
                      <Area
                        type="monotone"
                        dataKey="internal"
                        stroke="#2563eb"
                        strokeWidth={2.25}
                        fill="url(#internalActivityFill)"
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: '#2563eb' }}
                        name="Internal"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Rooms Section */}
      <div className="space-y-4 mt-4" data-tour="rooms-container">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-between border-b border-slate-200 pb-1">
          <div className="flex items-center gap-4 sm:gap-8 flex-1 min-w-0 overflow-x-auto">
            <button
              onClick={() => setActiveTab("my-rooms")}
              className={cn(
                "pb-3 text-sm font-medium transition-all relative",
                activeTab === "my-rooms"
                  ? "text-blue-600"
                  : "text-slate-500 hover:text-slate-700"
              )}
            >
              My Rooms
              <span className={cn(
                "ml-2 px-2 py-0.5 rounded-full text-xs transition-colors",
                activeTab === "my-rooms" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
              )}>
                {myRoomsCount}
              </span>
              {activeTab === "my-rooms" && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("shared-rooms")}
              className={cn(
                "pb-3 text-sm font-medium transition-all relative",
                activeTab === "shared-rooms"
                  ? "text-blue-600"
                  : "text-slate-500 hover:text-slate-700"
              )}
            >
              Shared with me
              <span className={cn(
                "ml-2 px-2 py-0.5 rounded-full text-xs transition-colors",
                activeTab === "shared-rooms" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
              )}>
                {sharedRoomsCount}
              </span>
              {activeTab === "shared-rooms" && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full" />
              )}
            </button>
          </div>
          {activeTab === "my-rooms" && (
            <Select value={statusFilter} onValueChange={(value: 'all' | 'DRAFT' | 'ACTIVE' | 'ARCHIVED') => setStatusFilter(value)}>
              <SelectTrigger className="w-full sm:w-[140px] h-8 text-xs">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {activeTab === "my-rooms" && (
          <Card className="border-0 shadow-none bg-transparent">
            <CardContent className="p-0">
              {isLoading ? (
                <LoadingSkeleton />
              ) : rooms.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-12 bg-white rounded-2xl border border-dashed border-slate-300">
                  <div className="bg-blue-50 p-4 rounded-full mb-4">
                    <LayoutGrid className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-slate-900">
                    {isSmartShare ? 'Start with Smart Share' : 'No Rooms Found'}
                  </h3>
                  <p className="text-slate-500 mb-6 max-w-md">
                    {isSmartShare
                      ? 'Upload a document and send a professional share link in minutes.'
                      : statusFilter !== 'all'
                        ? `You don't have any ${statusFilter.toLowerCase()} rooms yet.`
                        : "Get started by creating your first room from the dashboard."
                    }
                  </p>
                  <div className="flex space-x-4">
                    {isSmartShare ? (
                      <Button asChild className="bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded-xl px-6">
                        <Link href="/smart-share">
                          <Share2 className="mr-2 h-4 w-4" /> Smart Share
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setShowCreateRoom(true)}
                        className="bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded-xl px-6"
                      >
                        <PlusCircle className="mr-2 h-4 w-4" /> Create Room
                      </Button>
                    )}
                    <Link href="/?tour=dashboard">
                      <Button variant="outline" className="text-slate-600 border-slate-200 hover:bg-slate-50 transition-colors rounded-xl">
                        <PlayCircle className="mr-2 h-4 w-4" /> Watch Tutorial
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-[55vh] md:max-h-[450px] overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                  {rooms.map((room) => (
                    <div
                      key={room.id}
                      className={cn(
                        "group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 p-3 rounded-xl border transition-all duration-200 cursor-pointer relative overflow-hidden",
                        "bg-white border-slate-100 hover:border-blue-200 hover:shadow-md hover:shadow-blue-500/5"
                      )}
                      onClick={() => handleRoomClick(room.id)}
                    >
                      <div className="flex items-center space-x-3 flex-grow min-w-0">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center text-base font-bold shadow-sm",
                          room.iconBg || "bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600"
                        )}>
                          {room.name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{room.name}</h3>
                            {room.isExample && (
                              <Badge variant="secondary" className="text-[10px] uppercase tracking-wide font-bold">
                                Example
                              </Badge>
                            )}
                            {room.status && room.status !== 'ACTIVE' && (
                              <Badge
                                variant={room.status === 'DRAFT' ? 'secondary' : 'outline'}
                                className={cn(
                                  "text-[10px] uppercase tracking-wide font-bold",
                                  room.status === 'DRAFT' && "bg-yellow-100 text-yellow-700 hover:bg-yellow-100",
                                  room.status === 'ARCHIVED' && "bg-gray-100 text-gray-700 hover:bg-gray-100"
                                )}
                              >
                                {room.status}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 flex items-center flex-wrap gap-y-1 mt-0.5">
                            <RoomListAccountLabel room={room} />
                            {room.members > 0 && (
                              <>
                                <span className="mx-2 text-slate-300">•</span>
                                <span className="flex items-center">
                                  <Users className="w-3 h-3 mr-1" />
                                  {room.members}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex w-full sm:w-auto items-center justify-between sm:justify-end gap-2 sm:gap-4 text-sm">
                        <div className="text-left sm:text-right min-w-[100px]">
                          {(() => {
                            const badgeConfig = getActivityBadgeConfig(room.lastActivity, room.customerActivity);
                            return (
                              <Badge
                                variant="outline"
                                className={cn("font-medium border", badgeConfig.className)}
                              >
                                {badgeConfig.text}
                              </Badge>
                            );
                          })()}
                          <div className="flex items-center justify-end text-slate-400 text-xs mt-1.5">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatLastActivity(room.lastActivity || new Date().toISOString())}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-colors opacity-60 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlaybookClick(room.id);
                            }}
                            title="Playbook"
                          >
                            <PlayCircle size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-colors opacity-60 group-hover:opacity-100"
                            onClick={(e) => handleAnalyticsClick(room, e)}
                            title="Analytics"
                          >
                            <BarChart2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-colors opacity-60 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareRoom(room);
                            }}
                            title="Share"
                          >
                            <Share2 size={16} />
                          </Button>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors opacity-0 group-hover:opacity-100"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleEditRoom(room.id);
                              }}>
                                <Edit className="mr-2 h-4 w-4" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => {
                                void handleManageProducts(room, e);
                              }}>
                                <PackagePlus className="mr-2 h-4 w-4" /> Products
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRoom(room.id);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "shared-rooms" && (
          <Card className="border-0 shadow-none bg-transparent">
            <CardContent className="p-0">
              {isLoadingSharedRooms ? (
                <LoadingSkeleton />
              ) : sharedRooms.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-12 bg-white rounded-2xl border border-dashed border-slate-300">
                  <div className="bg-slate-50 p-4 rounded-full mb-4">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-slate-900">No Shared Rooms</h3>
                  <p className="text-slate-500 mb-6 max-w-md">
                    Rooms shared with you from other tenants will appear here.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-[55vh] md:max-h-[450px] overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                  {sharedRooms.map((room) => (
                    <div
                      key={room.id}
                      className={cn(
                        "group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 p-3 rounded-xl border transition-all duration-200 cursor-pointer relative overflow-hidden",
                        "bg-white border-slate-100 hover:border-blue-200 hover:shadow-md hover:shadow-blue-500/5"
                      )}
                      onClick={() => handleRoomClick(room.id)}
                    >
                      <div className="flex items-center space-x-3 flex-grow min-w-0">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center text-base font-bold shadow-sm",
                          room.iconBg || "bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600"
                        )}>
                          {room.name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{room.name}</h3>
                            {room.tenant && (
                              <Badge variant="outline" className="text-xs">
                                {room.tenant.name}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 flex items-center flex-wrap gap-y-1 mt-0.5">
                            <RoomListAccountLabel room={room} />
                            {room.members > 0 && (
                              <>
                                <span className="mx-2 text-slate-300">•</span>
                                <span className="flex items-center">
                                  <Users className="w-3 h-3 mr-1" />
                                  {room.members}
                                </span>
                              </>
                            )}
                            {room.owner?.name && (
                              <>
                                <span className="mx-2 text-slate-300">•</span>
                                <span className="text-slate-400">by {room.owner.name}</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex w-full sm:w-auto items-center justify-between sm:justify-end gap-2 sm:gap-4 text-sm">
                        <div className="text-left sm:text-right min-w-[100px]">
                          {(() => {
                            const badgeConfig = getActivityBadgeConfig(room.lastActivity, room.customerActivity);
                            return (
                              <Badge
                                variant="outline"
                                className={cn("font-medium border", badgeConfig.className)}
                              >
                                {badgeConfig.text}
                              </Badge>
                            );
                          })()}
                          <div className="flex items-center justify-end text-slate-400 text-xs mt-1.5">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatLastActivity(room.lastActivity || new Date().toISOString())}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <DashboardUsageInsights
        stats={analyticsStats}
        isLoadingStats={isLoadingStats}
        rooms={activeTab === 'shared' ? sharedRooms : rooms}
      />
      </>
      )}

      {/* Modals and Dialogs */}
      <ShareRoomModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        roomId={selectedRoom?.id || ''}
        roomName={selectedRoom?.name || ''}
        accountId={selectedRoom?.account?.id || ''}
        accountName={selectedRoom?.account?.name || ''}
        roomType={selectedRoom?.roomType as 'ROOM' | 'COMMUNITY' | 'TEMPLATE' | undefined}
        domainJoinEnabled={Boolean(selectedRoom?.domainJoinEnabled)}
        allowedJoinDomains={selectedRoom?.allowedJoinDomains ?? []}
      />

      {showCreateRoom && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto shadow-2xl">
            <CreateSmartRoomComponent
              onClose={() => setShowCreateRoom(false)}
              onRoomCreated={handleRoomCreated}
            />
          </div>
        </div>
      )}

      <Dialog open={isProductsDialogOpen} onOpenChange={setIsProductsDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Room Products</DialogTitle>
            <DialogDescription>
              {selectedRoomForProducts
                ? `Add or remove products linked to ${selectedRoomForProducts.name}.`
                : 'Add or remove products linked to this room.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={isLoadingProductOptions ? "Loading products..." : "Select a product to add"} />
                </SelectTrigger>
                <SelectContent>
                  {unlinkedProducts.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      {isLoadingProductOptions ? 'Loading...' : 'No available products'}
                    </SelectItem>
                  ) : (
                    unlinkedProducts.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <Button
                onClick={() => void handleAddProductToRoom()}
                disabled={!selectedProductId || isSavingRoomProduct || isLoadingProductOptions}
              >
                {isSavingRoomProduct ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Add
              </Button>
            </div>

            <div className="rounded-lg border p-3 max-h-[320px] overflow-y-auto space-y-2">
              {isLoadingRoomProducts ? (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading room products...
                </div>
              ) : roomProducts.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No products linked yet.
                </p>
              ) : (
                roomProducts.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-3 rounded-md border bg-slate-50/70 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{item.product.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.product.productType.replaceAll('_', ' ')}
                        {item.product.listPrice
                          ? ` • ${item.product.currency || 'USD'} ${item.product.listPrice}`
                          : ''}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={removingProductId === item.productId}
                      onClick={() => void handleRemoveProductFromRoom(item.productId)}
                    >
                      {removingProductId === item.productId ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : null}
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Room</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this room? This action cannot be undone and will remove all associated data and conversations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 rounded-xl"
            >
              Delete Room
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <JourneySheet
        open={isJourneySheetOpen}
        onOpenChange={setIsJourneySheetOpen}
        roomId={selectedRoomForAnalytics?.id || ''}
      />
      {!isSmartShare && !isLoading && rooms.some((room) => !room.isExample) && (
        <FeatureTour autoStart />
      )}
    </div>
  )
}
